import { graphql, useQuery } from "@opensea/graphql"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../constants"

const ItemAttributeQuery = graphql(`
  query ItemAttributeQuery(
    $traitType: String!
    $value: String!
    $collectionId: String!
  ) {
    itemAttribute(
      attribute: {
        traitType: $traitType
        value: $value
        collectionId: $collectionId
      }
    ) {
      traitType
      value
      floorPrice {
        token {
          unit
        }
      }
      topOfferPrice {
        token {
          unit
        }
      }
    }
  }
`)

type UseItemAttributeProps = {
  traitType: string
  value: string
  collectionId: string
  pause?: boolean
}

export function useItemAttribute({
  traitType,
  value,
  collectionId,
  pause,
}: UseItemAttributeProps) {
  const [{ data, fetching, error }] = useQuery({
    query: ItemAttributeQuery,
    variables: {
      traitType,
      value,
      collectionId,
    },
    pause,
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  return {
    attribute: data?.itemAttribute,
    fetching,
    error,
  }
}

type UseItemAttributesProps = {
  traits: Array<{
    traitType: string
    value: string
  }>
  collectionId: string
  pause?: boolean
}

export function useItemAttributes({
  traits,
  collectionId,
  pause,
}: UseItemAttributesProps) {
  // Support up to 2 traits with fixed hooks to avoid Rules of Hooks violations
  const shouldPause = pause || traits.length === 0

  const [{ data: trait1Data, fetching: trait1Fetching, error: trait1Error }] =
    useQuery({
      query: ItemAttributeQuery,
      variables: {
        traitType: traits[0]?.traitType || "",
        value: traits[0]?.value || "",
        collectionId,
      },
      pause: shouldPause || traits.length < 1,
      context: NETWORK_ONLY_OPERATION_CONTEXT,
    })

  const [{ data: trait2Data, fetching: trait2Fetching, error: trait2Error }] =
    useQuery({
      query: ItemAttributeQuery,
      variables: {
        traitType: traits[1]?.traitType || "",
        value: traits[1]?.value || "",
        collectionId,
      },
      pause: shouldPause || traits.length < 2,
      context: NETWORK_ONLY_OPERATION_CONTEXT,
    })

  // Collect all valid attributes (limited by MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS)
  const attributes = [
    trait1Data?.itemAttribute,
    trait2Data?.itemAttribute,
  ].filter(Boolean)

  // Aggregate loading and error states
  const fetching = trait1Fetching || trait2Fetching
  const errors = [trait1Error, trait2Error].filter(Boolean)
  const error = errors.length > 0 ? errors[0] : undefined

  return {
    attributes,
    fetching,
    error,
  }
}
